import { treaty } from '@elysia/eden'
// TYPE-ONLY. A value import here would bundle the entire server — Drizzle,
// Postgres driver and all — into the mobile app. Enforced by an ESLint rule.
import type { App } from '@church/api'

/**
 * Eden Treaty client factory.
 *
 * There is no codegen step and no OpenAPI round-trip: the client's types are
 * inferred straight from the server's `App` type. Rename a field in a route and
 * every call site in web and mobile fails to compile.
 */

/**
 * Where the access token lives. Web uses httpOnly cookies (so this returns
 * null and credentials ride along automatically); mobile uses SecureStore.
 */
export interface TokenStore {
  get(): string | null | Promise<string | null>
  set(token: string | null): void | Promise<void>
}

export interface ClientOptions {
  /** Omit on web, where the session cookie is sent automatically. */
  tokens?: TokenStore
  /**
   * Send cookies on cross-origin requests. Web only.
   *
   * Spelled out rather than using the DOM's `RequestCredentials`, because this
   * package also compiles for React Native, which has no DOM lib.
   */
  credentials?: 'omit' | 'same-origin' | 'include'
  /** Extra headers merged into every request (e.g. the active organization). */
  headers?: () => Record<string, string>
  /**
   * Fires on any 401 from the API — the session is gone (expired, revoked, or
   * signed out elsewhere). Apps route to sign-in here; there is nothing to
   * silently retry, because better-auth sessions extend server-side on use
   * and a 401 means this one no longer exists.
   */
  onUnauthorized?: () => void
}

export function createClient(baseUrl: string, options: ClientOptions = {}) {
  const { tokens, credentials, headers, onUnauthorized } = options

  return treaty<App>(baseUrl, {
    /**
     * Eden revives anything date-shaped into a `Date` by default, including a
     * plain `2026-09-01`. That silently contradicts the inferred types — the
     * endpoint says `string`, the client hands back an object — and the first
     * place it bit was a meeting addressed by its date: the path segment
     * became `Mon Oct 05 2026 19:00:00 GMT-0500 (…)` and the API 422'd.
     *
     * Off, everywhere. The wire format is JSON, JSON has no date type, and a
     * screen that wants a `Date` says `new Date(...)` where it is obvious.
     */
    parseDate: false,
    fetch: credentials ? { credentials } : undefined,
    // Object.assign keeps the wrapper structurally a `typeof fetch` (Bun's
    // type declares statics like `preconnect` that the bare closure lacks).
    fetcher: onUnauthorized
      ? Object.assign(async (input: Parameters<typeof fetch>[0], init?: RequestInit) => {
          const response = await fetch(input, init)
          if (response.status === 401) onUnauthorized()
          return response
        }, fetch)
      : undefined,
    headers: async () => {
      const extra = headers?.() ?? {}
      const token = await tokens?.get()
      return token ? { ...extra, authorization: `Bearer ${token}` } : extra
    },
  })
}

export type ApiClient = ReturnType<typeof createClient>

/** In-memory token store. Useful for tests; never for a real client. */
export function memoryTokenStore(initial: string | null = null): TokenStore {
  let token = initial
  return {
    get: () => token,
    set: (next) => {
      token = next
    },
  }
}

export { createWebAuthClient, sharedAuthPlugins } from './auth.ts'
export type { AuthClient } from './auth.ts'
